import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'CartDrawer' }

import { computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import { formatPhoneDisplay } from '@/util/phoneFormatter'

type CartLine = { name: string; meta: string; qty: number; amount: number | null }
// Bookings carry no stored price on the cart, so their amount is blank; items and
// gift cards use the value stored on their link rows.

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
  cart: { type: Object as () => any, default: null },
  /** 0 abandoned, 1 active, 2 errors: drives which actions and rows show. */
  tab: { type: Number, default: 0 },
  formatAmount: { type: Function as unknown as () => (v: any) => string, required: true },
  formatDate: { type: Function as unknown as () => (d: string) => string, required: true },
  formatTime: { type: Function as unknown as () => (t: string) => string, required: true },
  formatDateTime: { type: Function as unknown as () => (d: string) => string, required: true },
},
  emits: ['update:show', 'close', 'delete', 'send-email'],
  setup(__props, { emit }) {

const props = __props

// Read-only detail for one cart row from the carts list. The parent passes the row
// as loaded by get-active-carts (bookings_in_cart etc. already parsed) plus the
// group-aware formatters, so nothing here re-fetches or re-formats.


const { proxy } = getCurrentInstance() as any

const title = computed(() => {
  const c = props.cart
  if (!c) return 'Cart'
  const name = [c.customer_first_name, c.customer_last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  if (c.customer_email) return c.customer_email
  return 'Anonymous customer'
})
const showEmailStatus = computed(() => props.tab === 0)
const canDelete = computed(() => props.tab === 0 && !!props.cart)
const cartLines = computed<CartLine[]>(() => {
  const c = props.cart
  if (!c) return []
  const num = (v: any) => (v == null || v === '' || Number.isNaN(Number(v))) ? null : Number(v)
  const bookings = (c.bookings_in_cart || []).map((b: any) => ({ name: b.game_name, meta: `${props.formatDate(b.booking_date)} · ${props.formatTime(b.start_time)}`, qty: Number(b.quantity) || 1, amount: num(b.value) }))
  const items = (c.items_in_cart || []).map((i: any) => ({ name: i.name, meta: '', qty: Number(i.quantity) || 1, amount: num(i.value) }))
  const gifts = (c.gift_cards_in_cart || []).map((g: any) => ({ name: g.name, meta: '', qty: Number(g.quantity) || 1, amount: num(g.value) == null ? null : (num(g.value) as number) * (Number(g.quantity) || 1) }))
  return [...bookings, ...items, ...gifts]
})
const errors = computed(() => {
  if (!props.cart) return []
  if (Array.isArray(props.cart.errors)) return props.cart.errors
  // An errors-tab row is itself one error joined to its cart.
  if (props.cart.error_type) return [props.cart]
  return []
})

function groupCountry(): string {
  try { return JSON.parse(localStorage.getItem('group') || '{}').country || 'US' } catch (e) { return 'US' }
}
function formatPhone(phone: string) {
  return formatPhoneDisplay(phone, props.cart?.country || groupCountry())
}
function capitalize(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : ''
}
function promoCodeOf(error: any): string {
  try {
    const data = typeof error.error_data === 'string' ? JSON.parse(error.error_data) : error.error_data
    return data?.promo_code || data?.promoCode || ''
  } catch (e) { return '' }
}
async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    proxy.$notify({ type: 'success', message: 'Copied', timeout: 1500 })
  } catch (e) {
    proxy.$notify({ type: 'warning', message: 'Could not copy', timeout: 2000 })
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}
function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}
watch(() => props.show, (visible) => {
  if (visible) document.addEventListener('keydown', onEscKey)
  else document.removeEventListener('keydown', onEscKey)
}, { immediate: true })
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))

return { __sfc: true,props, emit, proxy, title, showEmailStatus, canDelete, cartLines, errors, groupCountry, formatPhone, capitalize, promoCodeOf, copy, onClose, onEscKey }
}

})